import { Lightbulb } from "lucide-react";
import styles from "./changeLog.module.scss";
import { GeneralLink } from "components/Footer/FooterLink";
import { findIndex, get, identity, keyBy, mapValues } from "lodash";
import { CHARACTERS_COLLECTION } from "contexts/database/useCharactersAccessors";
import { supportEmail } from "constants/contactInfo";
import { BULLET_LIST_ITEM_TYPE } from "components/Common/List/bulletListConsts";

// const wipNextVersion =

export const stableVersion = "1.7.4";

const Link = (props) => (
  <GeneralLink {...props} className={styles["version-link"]} />
);
const InlineIcon = ({ icon: Icon, ...rest }) => (
  <Icon
    style={{
      verticalAlign: "middle",
      width: "15px",
      height: "15px",
      ...rest,
    }}
  />
);
const Tip = ({ children }) => (
  <span className={styles["tip"]}>
    <InlineIcon icon={Lightbulb} /> Tip: {children}
  </span>
);

export const versions = [
  {
    version: "2.1.1",
    date: "2026-03-16",
    changes: [
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Fixed issue with deleting Picotitans from a group",
      },
    ],
  },
  {
    version: "2.1.0",
    date: "2026-01-31",
    changes: [
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Adjusted several aspects, augments, and presets to match final release",
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: <span>Notify us at <u>{supportEmail}</u> if you encounter any additional inconsistencies or bugs</span>
      },
    ],
  },
  {
    version: "2.0.1",
    date: "2025-08-24",
    changes: [
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Added/tweaked aspects (Build, Mounts, Tools)",
      },
    ],
  },
  {
    version: "2.0.0",
    date: "2025-08-19",
    description: (
      <div>
        <b>PICO Limon Playtest Update!</b>
        <br />
        This is an ambitious version, so I know there's likely bugs (pun
        intended) and missing features. Please reach out to our support email:{" "}
        <u>{supportEmail}</u> with any issues you encounter.
      </div>
    ),
    changes: [
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Modified layout of Character and Picotitan sheets",
        items: [
          "Added 'Edit Layout' button in the header (next to the settings button)",
          "If you had an existing sheet, some sections might be out of order",
          "We recommend opening the Layout Editor and clicking 'Reset to Default'",
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Character Sheet",
        items: [
          "Re-named skills to match playtest",
          "Added new moods",
          "Re-styled skills and mood sections",
          "Added 'Add Preset' button to several sections",
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Picotitan Sheet",
        items: [
          "Implemented remaining picotitan feature sections",
          {
            type: BULLET_LIST_ITEM_TYPE.NORMAL,
            text: "Added 'Select Preset' button at the top of the screen",
            items: [
              "Updates the selected sheet to match a specific Picotitan preset",
              "This is a temporary solution for a Picotitan Wizard-esque experience",
              "We're still working on an actual Wizard",
            ],
          },
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "GM Sheet",
        items: [
          "Fixed incorrect character preview values",

          {
            type: BULLET_LIST_ITEM_TYPE.NORMAL,
            text: "Added ability to swap the tracks section between 'GM Tracks' and 'Shared Tracks'",
            items: [
              "GM Tracks are only present on the GM page",
              "Shared Tracks are visible on each sheet type and by each member of your Group",
            ],
          },
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Added 'Shared Tracks' section to all sheets. These tracks are synced across all members of the group",
        items: [
          "Utilize this section to share important tracks that all players should be aware of",
          "Contains new 'Journey' track",
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Next Up:",
        items: [
          "Bugfixes probably :)",
          "Update preset aspects to match playtest",
          "Picotitan Wizard",
          "Improved default images",
          "More Help Tooltips",
          "Tutorials",
          "A survey for feedback on newly added features and missing features",
        ],
      },
    ],
  },
  {
    version: "1.3.0",
    date: "2025-02-17",
    changes: [
      {
        type: BULLET_LIST_ITEM_TYPE.IMPORTANT,
        text: "New Features",
        items: [
          "Added ability to search for text in Aspects during Character Creation",
          {
            type: BULLET_LIST_ITEM_TYPE.NORMAL,
            text: "Added setting to change the color of Heart-Tracks such as Aspects.",
            items: [
              "The default is now red instead of your secondary theme color",
              'Look for "Fully Colored Skills" in the "General Settings".',
            ],
          },
          {
            type: BULLET_LIST_ITEM_TYPE.NORMAL,
            text: "Added setting to reduce the amount of color in the Skills section",
            items: [
              "When disabled, it moves the background color from the entire category section to just the category label.",
              'Look for "Heart-Track Color" in the "General Settings".',
            ],
          },
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Styling",
        items: [
          "Added animations to the Mood section on the Character sheet",
          "Re-arranged and re-styled Aspects page in the Character Creator",
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Usability",
        items: [
          "Added tooltips explaining mood, bits, and bobs",
          'Adjusted "Add Existing" interface to show all aspects by default',
        ],
      },
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Bugfixes",
        items: [
          "Missing pseudos, labels, etc.",
          "Fixed crash when importing a sheet into a group with no sheets.",
          "Removed Wildsea rollers from the Presets on the GM page.",
        ],
      },
    ],
  },
  {
    version: "1.2.0",
    date: "2025-02-15",
    changes: [
      {
        type: BULLET_LIST_ITEM_TYPE.NORMAL,
        text: "Added Picotitan Page",
        items: [
          "Very rough implementation, will be improved in later updates",
          "Should contain the basics for tracking Picotitans.",
        ],
      },
      "Tweaked how an aspect's name is modified by appending an augment",
      "Added warning when selecting 3 ranks in a skill during character creation",
      "Styling tweaks",
    ],
  },
  {
    version: "1.1.0",
    date: "2025-02-14",
    changes: [
      'Several "Koilfruit Playtest" alignments (aspects, augments, skills, etc)',
      <span>
        Please notify {supportEmail} of any bugs or inconsistencies on the site.
      </span>,
    ],
  },
  {
    version: "1.0.1",
    date: "2025-02-12",
    migrations: {
      [CHARACTERS_COLLECTION]: ({ oldData, newData, changes }) => {
        const { background, majorMilestones, minorMilestones, bits, bobs } =
          newData;
        const { bloodline, origin, pseudo, bug, ...oldBackground } =
          background || {};
        const newBackground = {
          ...oldBackground,
          pseudo: pseudo || bloodline || "",
          bug: bug || origin || "",
        };

        const newChanges = {
          ...changes,
          background: newBackground,
          bits: bits || majorMilestones || [],
          bobs: bobs || minorMilestones || [],
        };

        return {
          oldData,
          newData: {
            ...newData,
            ...newChanges,
          },
          changes: newChanges,
        };
      },
    },
    changes: [
      "Implemented character creator",
      "Fixed tons of bugs (no pun intended)",
    ],
  },
  {
    version: "1.0.0",
    date: "2025-01-03",
    changes: ["Initial launch! 🎉"],
  },
];

export const surveyVersionsList = versions
  .filter((v) => v.survey)
  .map((v) => v.version);
export const surveyVersions = mapValues(
  keyBy(surveyVersionsList, identity),
  () => true
);

export const currentVersion = versions[0].version;
export const latestSurveyVersionIndex = findIndex(versions, (v) => v.survey);
export const latestSurveyVersion = get(versions, [
  latestSurveyVersionIndex,
  "version",
]);
